import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { IconArrowLeft, IconChevronDown } from "@tabler/icons-react";
import { useUsuario } from "../../../../providers/usuarioProvider";
import { useSnackBar } from "../../../../stores/snackBarStores";
import UploadInternacional from "../../../../models/uploadInternacional";
import OperacaoInternacional from "../../../../models/operacaoInternacional";
import { DebitoCredito } from "../../../../models/enum";
import UploadInternacionalDb from "../../../../db/uploadInternacionalDb";
import OperacaoInternacionalDb from "../../../../db/operacaoInternacionalDb";
import { deleteDoc, onSnapshot, query, where } from "firebase/firestore";
import CabecalhoStatement from "./cabecalhoStatement";
import TabelaOperacoesInternacionais from "../../../util/tabelas/tabelaOperacoesInternacionais";
import moment from "moment-timezone";
import { groupBy, map, orderBy } from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import { radius } from "../../../../theme";

// Esse componente é carregado pela rota /arquivo/statement/:uploadId
// A prop uploadId é injetada via Router.

export default function Statement() {
  const { uploadId } = useParams();

  const navigate = useNavigate();
  const { visualizado } = useUsuario();
  const snackBar = useSnackBar();
  const [upload, setUpload] = useState<UploadInternacional>();
  const [operacoes, setOperacoes] = useState<OperacaoInternacional[]>([]);
  const [abrirPopupExclusao, setAbrirPopupExclusao] = useState(false);

  // Carrega o upload
  useEffect(() => {
    if (!!visualizado && !!uploadId) {
      return onSnapshot(
        new UploadInternacionalDb().doc({
          usuarioId: visualizado,
          uploadId,
        }),
        (snapshot) => {
          const up = snapshot.data() as UploadInternacional;
          setUpload(up);
          if (up?.operacoes && up.operacoes.length > 0) {
            setOperacoes(up.operacoes);
          }
        },
      );
    }
  }, [visualizado, uploadId]);

  // Se não houver operações no documento, busca na coleção operacoesInternacionais
  useEffect(() => {
    if (
      !!visualizado &&
      !!upload &&
      (!upload.operacoes || upload.operacoes.length === 0)
    ) {
      const db = new OperacaoInternacionalDb();
      const q = query(
        db.collection({ usuarioId: visualizado, operacaoInternacionalId: "" }),
        where("uploadId", "==", upload.id),
      );
      return onSnapshot(q, (snapshot) => {
        setOperacoes(
          snapshot.docs.map((doc) => doc.data() as OperacaoInternacional),
        );
      });
    }
  }, [visualizado, upload]);

  if (!upload) return <></>;

  // Agrupa operações por moeda e depois por tipo
  const operacoesPorMoeda = groupBy(operacoes, "moeda");

  const exportarCsv = () => {
    if (!operacoes || operacoes.length === 0) return;

    const header = [
      "Data",
      "Ativo",
      "Descrição",
      "Tipo",
      "Quantidade",
      "Valor",
      "Valor (R$)",
      "Moeda",
      "Crédito/Débito",
    ];

    const linhas = operacoes.map((op) => [
      moment(op.dataPregao).format("DD/MM/YY"),
      op.ativo ?? "",
      op.descricao,
      (op.tipoOperacao ?? "") + (op.duplicataDeUploadId ? " (repetida)" : ""),
      op.quantidade !== undefined
        ? op.quantidade.toLocaleString("pt-BR", {
            maximumFractionDigits: 3,
          })
        : "",
      op.valorOperacao.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      op.valorOperacaoBRL !== undefined
        ? op.valorOperacaoBRL.toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : "",
      op.moeda,
      op.debitoCredito === DebitoCredito.Debito ? "Débito" : "Crédito",
    ]);

    const csvContent = [header, ...linhas]
      .map((linha) =>
        linha
          .map((campo) => {
            const texto = String(campo).replace(/"/g, '""');
            return `"${texto}"`;
          })
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const nomeArquivo = `statement_${upload.id}.csv`;
    link.setAttribute("download", nomeArquivo);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const apagarStatement = async () => {
    if (!!visualizado) {
      await deleteDoc(
        new UploadInternacionalDb().doc({
          usuarioId: visualizado,
          uploadId: upload.id,
        }),
      ).then(() => {
        navigate(-1);
        snackBar({
          type: "ALERT",
          payload: {
            mensagem: "Statement apagado com sucesso!",
            severidade: "success",
          },
        });
      });
    }
  };

  return (
    <Container maxWidth="lg">
      {/* Barra de ações */}
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-start"
        pt={4}
        pb={2}
      >
        <Box flexGrow={1} />
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => navigate(-1)}
          startIcon={<IconArrowLeft />}
        >
          Voltar
        </Button>
        <Box m={1 / 2} />
      </Box>

      {/* Dialog de confirmação */}
      <Dialog
        open={abrirPopupExclusao}
        onClose={() => setAbrirPopupExclusao(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" variant="h3">
          Quer mesmo apagar esse statement?
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            variant="body1"
            color="text.secondary"
          >
            Não será possível desfazer essa operação.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbrirPopupExclusao(false)} color="inherit">
            Cancelar
          </Button>
          <Button onClick={apagarStatement} variant="contained" color="error">
            Apagar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Conteúdo principal */}
      <Box pb={4}>
        <Box paddingX={1} paddingY={2}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12}>
              <CabecalhoStatement upload={upload} />
            </Grid>

            <Grid item xs={12}>
              <Box paddingX={1} paddingY={1}>
                {operacoes && operacoes.length > 0 ? (
                  <Box>
                    {map(operacoesPorMoeda, (operacoesMoeda, moeda) => {
                      // Ordena as operações por tipo dentro de cada moeda
                      const operacoesOrdenadas = orderBy(
                        operacoesMoeda,
                        ["tipoOperacao", "dataPregao"],
                        ["asc", "desc"],
                      );

                      return (
                        <Box key={moeda} mb={3}>
                          <Accordion
                            defaultExpanded
                            disableGutters
                            elevation={0}
                            sx={(theme) => ({
                              // Hierarquia por hairline, não por sombra.
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: `${radius.xl}px`,
                              overflow: "hidden",
                              "&:before": { display: "none" },
                            })}
                          >
                            <AccordionSummary
                              expandIcon={<IconChevronDown />}
                              sx={(theme) => ({
                                backgroundColor: theme.palette.ink["08"],
                                "& .MuiAccordionSummary-content": {
                                  alignItems: "center",
                                },
                              })}
                            >
                              <Box
                                display="flex"
                                alignItems="baseline"
                                width="100%"
                                mr={2}
                                gap={1}
                              >
                                <Typography
                                  variant="dataMono"
                                  fontSize={16}
                                  fontWeight={600}
                                  color="text.primary"
                                >
                                  {moeda}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  ({operacoesMoeda.length} operações)
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              <TabelaOperacoesInternacionais
                                operacoes={operacoesOrdenadas}
                                showDate={true}
                              />
                            </AccordionDetails>
                          </Accordion>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Box
                    sx={(theme) => ({
                      border: `1px dashed ${theme.palette.divider}`,
                      borderRadius: `${radius.xl}px`,
                      py: 6,
                      textAlign: "center",
                    })}
                  >
                    <Typography variant="labelMono" color="text.secondary">
                      Nenhuma operação encontrada neste statement
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
}
